import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Check, Info, IdCard, Receipt, Scale, Banknote, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { FLAGS } from "@/lib/utils";

const SECTION_ICONS = { id: IdCard, tax: Receipt, legal: Scale, money: Banknote };

function StepItem({ step, done, onToggle }) {
  return (
    <button
      type="button"
      data-testid={`guide-step-${step.id}`}
      onClick={() => onToggle(step.id)}
      className={`w-full text-left flex items-start gap-3 p-4 rounded-2xl border transition-colors ${done ? "bg-propera-light/50 border-propera/25" : "bg-white border-neutral-200 hover:border-neutral-300"}`}
    >
      <span data-testid={`guide-step-check-${step.id}`} className={`mt-0.5 w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${done ? "bg-propera border-propera text-white" : "border-neutral-300"}`}>
        {done && <Check size={12} strokeWidth={3} />}
      </span>
      <span className="min-w-0">
        <span className={`block text-sm font-semibold ${done ? "text-neutral-500 line-through decoration-neutral-300" : "text-neutral-900"}`}>{step.title}</span>
        {step.body && <span className="block text-sm text-neutral-600 mt-1 leading-relaxed">{step.body}</span>}
      </span>
    </button>
  );
}

function CountryProgress({ done, total }) {
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <div className="soft-card p-5 flex items-center gap-5" data-testid="guide-progress">
      <div className="relative w-16 h-16 flex-shrink-0">
        <svg viewBox="0 0 36 36" className="w-16 h-16 -rotate-90">
          <circle cx="18" cy="18" r="15.5" fill="none" stroke="#eef2ef" strokeWidth="3.5" />
          <circle cx="18" cy="18" r="15.5" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" className="text-propera transition-[stroke-dasharray] duration-500" strokeDasharray={`${(pct / 100) * 97.4} 97.4`} />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-sm font-bold tabular text-neutral-900">{pct}%</span>
      </div>
      <div>
        <p className="text-sm font-semibold text-neutral-900">{done} of {total} steps complete</p>
        <p className="text-xs text-neutral-500 mt-0.5">Your progress is saved to your account.</p>
      </div>
    </div>
  );
}

export default function InvestorGuide() {
  const [data, setData] = useState(null);
  const [completed, setCompleted] = useState([]);
  const [params, setParams] = useSearchParams();
  const country = params.get("country") || "Kenya";

  useEffect(() => {
    (async () => {
      const [g, p] = await Promise.all([api.get("/guide"), api.get("/guide/progress")]);
      setData(g.data); setCompleted(p.data.completed || []);
    })();
  }, []);

  const guide = useMemo(() => data?.countries.find((c) => c.country === country) || data?.countries[0], [data, country]);
  const stepIds = useMemo(() => (guide?.sections || []).flatMap((s) => s.steps.map((st) => st.id)), [guide]);

  async function toggle(stepId) {
    const prev = completed;
    setCompleted((c) => (c.includes(stepId) ? c.filter((x) => x !== stepId) : [...c, stepId]));
    try {
      const { data: res } = await api.post("/guide/progress/toggle", { step_id: stepId });
      setCompleted(res.completed);
    } catch { setCompleted(prev); toast.error("Could not save progress"); }
  }

  if (!data) return <p className="text-sm text-neutral-500 text-center py-20">Loading…</p>;
  const doneCount = stepIds.filter((id) => completed.includes(id)).length;

  return (
    <div className="max-w-4xl mx-auto" data-testid="investor-guide-page">
      <p className="text-xs uppercase tracking-[0.18em] text-neutral-500 font-semibold flex items-center gap-1.5"><ListChecks size={13} /> Investor Requirements Hub</p>
      <h1 className="text-4xl sm:text-5xl font-bold text-neutral-900 mt-2">Invest from abroad, step by step</h1>
      <p className="text-neutral-500 mt-3 max-w-2xl">The ID, tax, legal and money-transfer steps overseas investors should complete before buying property.</p>

      <div className="mt-6 rounded-2xl bg-amber-50 border border-amber-200 px-4 py-3 flex gap-3 text-sm text-amber-900" data-testid="guide-disclaimer">
        <Info size={16} className="flex-shrink-0 mt-0.5" />
        <p>{data.disclaimer}</p>
      </div>

      <div className="flex gap-2 mt-8 flex-wrap" role="tablist">
        {data.countries.map((c) => {
          const ids = c.sections.flatMap((s) => s.steps.map((st) => st.id));
          const n = ids.filter((id) => completed.includes(id)).length;
          return (
            <button key={c.country} role="tab" data-testid={`guide-tab-${c.country}`} onClick={() => setParams({ country: c.country }, { replace: true })} className={`px-5 py-2.5 rounded-full text-sm font-semibold border transition-colors ${guide.country === c.country ? "bg-propera text-white border-propera" : "bg-white border-neutral-200 hover:border-neutral-300 text-neutral-700"}`}>
              {FLAGS[c.country]} {c.country} <span className={`ml-1 text-xs ${guide.country === c.country ? "text-white/80" : "text-neutral-400"}`}>{n}/{ids.length}</span>
            </button>
          );
        })}
      </div>

      <div className="mt-6 grid gap-4 md:grid-cols-[1fr_auto] items-start">
        <p className="text-sm text-neutral-600 leading-relaxed" data-testid="guide-intro">{guide.intro}</p>
        <CountryProgress done={doneCount} total={stepIds.length} />
      </div>

      <div className="mt-8 space-y-10">
        {guide.sections.map((sec) => {
          const Icon = SECTION_ICONS[sec.id] || ListChecks;
          const n = sec.steps.filter((st) => completed.includes(st.id)).length;
          return (
            <section key={sec.id} data-testid={`guide-section-${sec.id}`}>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-bold text-neutral-900 flex items-center gap-2"><span className="w-8 h-8 rounded-xl bg-propera-light text-propera flex items-center justify-center"><Icon size={16} /></span>{sec.title}</h2>
                <span className="text-xs text-neutral-500 tabular">{n}/{sec.steps.length}</span>
              </div>
              <div className="space-y-2">
                {sec.steps.map((st) => <StepItem key={st.id} step={st} done={completed.includes(st.id)} onToggle={toggle} />)}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
